import { getActiveLink } from '@blockit/std/url/activeLink';
import { webConfig } from '@blockit/web-shared/config/webConfig';
import { CustomLoader } from '@components/CustomLoader';
import { PageContainer } from '@components/PageContainer';
import { useLogPageView } from '@features/analytics/hooks/useLogPageView';
import { useFirebaseAuthState } from '@features/auth/hooks/useFirebaseAuthState';
import { setVisitedBlockitAppCookie } from '@features/auth/utils/cookieUtils';
import { OnboardingStep } from '@features/onboarding/constants';
import { useEndOnboardingPreviewOutside } from '@features/onboarding/preview/onboardingPreviewStore';
import { onboardingStepLink } from '@features/onboarding/utils/onboardingUtils';
import { useUser } from '@features/users/api/getUser';
import { useGetUserAccessValidation } from '@features/users/api/getUserAccessValidation';
import { UserPlanType } from '@features/users/types/index';
import { ROUTES_TO_ROUTE_DETAILS } from '@routes/constants';
import { PrivateRoutes, PublicRoutes } from '@routes/enums';
import { createFileRoute, Navigate, Outlet, useLocation } from '@tanstack/react-router';
import { APIProvider } from '@vis.gl/react-google-maps';
import type { CSSProperties } from 'react';

const GOOGLE_MAPS_LIBRARIES = ['marker'];
const FULL_PAGE_LOADER_STYLE = { '--loader-size': '58px' } as CSSProperties;

const SignedInGate = () => {
  const authState = useFirebaseAuthState();
  const pathname = useLocation({ select: (location) => location.pathname });
  const activeLink = getActiveLink(Object.values(PrivateRoutes), pathname);
  const routeDetails = activeLink ? ROUTES_TO_ROUTE_DETAILS[activeLink] : null;
  const { data: user, isLoading: userIsLoading } = useUser();
  const { data: userAccessValidation, isLoading: userAccessValidationLoading } = useGetUserAccessValidation();

  useLogPageView(routeDetails?.title);
  useEndOnboardingPreviewOutside(pathname);

  if (authState.loading || userIsLoading || userAccessValidationLoading) {
    return (
      <PageContainer>
        <CustomLoader style={FULL_PAGE_LOADER_STYLE} />
      </PageContainer>
    );
  }
  if (!authState.signedIn) {
    return <Navigate to={PublicRoutes.LOGIN} />;
  }

  if (!user) {
    return (
      <PageContainer>
        <CustomLoader style={FULL_PAGE_LOADER_STYLE} />
      </PageContainer>
    );
  }
  if (user.suspendedAt) {
    return <Navigate to={PublicRoutes.LOGOUT} />;
  }

  if (userAccessValidation?.isOnBlocklist) {
    return <Navigate to={PublicRoutes.LOGOUT} />;
  }

  if (user.disqualifiedAt) {
    // Waitlisted users get an explanation (and an invite-code path back in)
    // instead of a silent logout. Checked after suspension/blocklist so those
    // harder gates still log out.
    return <Navigate to={PrivateRoutes.WAITLIST} replace />;
  }

  if (
    (user.effectivePlan ?? user.userPlan?.plan) === UserPlanType.NO_CC &&
    !pathname.startsWith(PrivateRoutes.ONBOARDING) &&
    !pathname.startsWith(PrivateRoutes.INVITE_DELEGATE)
  ) {
    return <Navigate {...onboardingStepLink(OnboardingStep.CALENDARS)} replace />;
  }

  setVisitedBlockitAppCookie();

  // Here, not at the app root: mounting the provider injects the Maps JS loader, and no public page uses Maps.
  return (
    <APIProvider apiKey={webConfig.googleMapsApiKey} libraries={GOOGLE_MAPS_LIBRARIES}>
      <Outlet />
    </APIProvider>
  );
};

export const Route = createFileRoute('/_authenticated')({ component: SignedInGate });
